<template>
  <v-dialog
    v-model="show"
    fullscreen
    hide-overlay
    persistent
    transition="dialog-bottom-transition"
    @keydown.capture.esc="$emit('close-report')"
    no-click-animation
    :scrollable="true"
  >
    <v-card
      id="itemAnalysis"
      class="position-relative d-flex overflow-hidden flex-column rounded-0 grey lighten-4"
    >
      <v-toolbar
        ref="titleBar"
        dark
        color="primary"
        rounded="0"
        class="agg-rpt-title-bar"
      >
        <v-btn outlined dark @click="$emit('close-report')" class="mr-4">
          <v-icon class="mr-1">$back</v-icon> Back
        </v-btn>
        <v-toolbar-title>{{
          label ? label : "Individual Item Analysis"
        }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          dark
          outlined
          class="mr-4 d-none d-md-inline"
          @click="generatePDF"
        >
          Export to PDF
        </v-btn>
        <v-btn dark outlined class=" d-none d-md-inline" @click="generateCSV">
          Export to Excel
        </v-btn>

        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              outlined
              dark
              elevation="0"
              v-bind="attrs"
              v-on="on"
              small
              class="d-inline d-md-none"
            >
              <font-awesome-icon
                class="mr-2"
                icon="download"
              ></font-awesome-icon>
              export
            </v-btn>
          </template>
          <v-list>
            <v-list-item link @click="generatePDF">
              <v-list-item-title> Export to PDF</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="generateCSV">
              <v-list-item-title> Export to Excel</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>

      <div class="d-flex overflow-hidden flex-fill">
        <v-card
          ref="menu"
          dense
          rounded="0"
          class="agg-rpt-menu overflow-hidden d-lg-flex flex-column flex-shrink-0 d-none"
        >
          <div class="secondary lighten-3 overflow-hidden d-flex flex-shrink-0">
            <div class="px-4 pb-4 pt-6 flex-fill overflow-hidden">
              <div class="d-flex align-center w-100 overflow-hidden">
                <font-awesome-icon
                  class="detail-icon"
                  icon="sitemap"
                ></font-awesome-icon>
                <div class="flex-fill ml-4 overflow-hidden">
                  <div class="text-subtitle-2 font-weight-bold">
                    District
                  </div>
                  <div class="text-subtitle-1 text-truncate">
                    <template v-if="district">{{ district.name }}</template>
                    <template v-else>All Districts</template>
                  </div>
                </div>
              </div>
              <div class="d-flex align-center w-100 mt-3 overflow-hidden ">
                <font-awesome-icon
                  class="detail-icon"
                  icon="school"
                ></font-awesome-icon>
                <div class="flex-fill ml-4 overflow-hidden">
                  <div class="text-subtitle-2 font-weight-bold">
                    School
                  </div>
                  <div class="text-subtitle-1 text-truncate">
                    <template v-if="school">{{ school.name }}</template>
                    <template v-else>All Schools</template>
                  </div>
                </div>
              </div>
            </div>

            <div class="detail-accent flex-shrink-0"></div>
          </div>
          <div
            class="overflow-y-auto flex-fill"
            v-if="survey && survey.categories"
          >
            <v-card-title>Survey Sections</v-card-title>

            <v-list-item
              link
              v-for="(category, index) in survey.categories.filter(
                (x) => x.questions && x.questions.some((y) => !hidden(y))
              )"
              :key="category.key"
              :class="{ 'primary white--text': currentSection == index }"
              dense
              @click="scrollTo(sectionDetails[index].top - 100)"
            >
              <v-list-item-content>
                <v-list-item-title>{{
                  category.displayName
                }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-card>
        <div
          ref="content"
          class="agg-rpt-content d-flex flex-fill flex-column position-relative"
          :style="`overflow-y: ${loading ? 'hidden' : 'auto'};`"
          v-scroll.self="setScrollPosition"
        >
          <v-container class="pa-0 pa-md-8" style="max-width: 1200px;">
            <div
              class="secondary lighten-3 overflow-hidden d-flex flex-column elevation-3 flex-shrink-0 mb-6 d-lg-none ma-0 ma-sm-4 mx-md-8"
              style="z-index: 1"
            >
              <div
                class="px-md-12 px-sm-8 px-6 pb-4 pt-6 flex-fill overflow-hidden"
              >
                <div class="d-flex align-center w-100 overflow-hidden">
                  <font-awesome-icon
                    class="detail-icon"
                    icon="sitemap"
                  ></font-awesome-icon>

                  <div class="flex-fill ml-4 overflow-hidden">
                    <div class="text-subtitle-2 font-weight-bold">
                      District
                    </div>
                    <div class="text-subtitle-1 text-truncate">
                      <template v-if="district">{{ district.name }}</template>
                      <template v-else>All Districts</template>
                    </div>
                  </div>
                </div>
                <div class="d-flex align-center w-100 mt-3 overflow-hidden ">
                  <font-awesome-icon
                    class="detail-icon"
                    icon="school"
                  ></font-awesome-icon>
                  <div class="flex-fill ml-4 overflow-hidden">
                    <div class="text-subtitle-2 font-weight-bold">
                      School
                    </div>
                    <div class="text-subtitle-1 text-truncate">
                      <template v-if="school">{{ school.name }}</template>
                      <template v-else>All Schools</template>
                    </div>
                  </div>
                </div>
              </div>

              <div class="detail-accent flex-shrink-0"></div>
            </div>

            <v-fade-transition>
              <div
                ref="loadingOverlay"
                class="flex-fill agg-rpt-loading-overlay grey lighten-4"
                v-if="loading"
              >
                <v-row
                  class="fill-height"
                  align-content="center"
                  justify="center"
                  style="width: 100%;"
                >
                  <v-col class="text-subtitle-1 text-center" cols="12">
                    Getting report data
                  </v-col>
                  <v-col cols="6">
                    <v-progress-linear
                      color="primary"
                      indeterminate
                      rounded
                      height="6"
                    ></v-progress-linear>
                  </v-col>
                </v-row>
              </div>
            </v-fade-transition>

            <div
              v-if="survey && survey.categories"
              class="pa-0 pa-sm-4 pa-md-8"
              :style="`opacity: ${loading ? 0 : 1};`"
            >
              <div
                ref="section"
                v-for="(category, i) in survey.categories.filter(
                  (x) => x.questions && x.questions.some((y) => !hidden(y))
                )"
                :key="category.key"
                :data-section="i"
              >
                <v-card-text class="pa-0">
                  <div>
                    <h4 class="text-h3 mb-4 ml-4">
                      {{ category.displayName }}
                    </h4>

                    <v-alert
                      v-if="category.key === 'NewTeacher'"
                      color="secondary lighten-3"
                      border="left"
                      elevation="1"
                    >
                      <v-icon class="mr-1 ml-1">$info</v-icon> Districts/Schools
                      with less than 5 responses will not display
                      <strong>New Teacher</strong> data.
                    </v-alert>

                    <v-alert
                      v-if="category.key === 'Time'"
                      color="secondary lighten-3"
                      border="left"
                      elevation="1"
                    >
                      <v-icon class="mr-1 ml-1">$info</v-icon> Districts with
                      less than 50% responses rate will not display
                      <strong>District</strong> data.
                    </v-alert>
                  </div>

                  <template v-for="question in category.questions">
                    <v-card
                      class="mb-8"
                      :key="question.key"
                      v-if="!hidden(question)"
                    >
                      <v-card-title class="text-subtitle-1">
                        <div class=" d-flex flex-start">
                          <div
                            v-if="
                              question.questionNumber &&
                                question.questionNumber.length > 0
                            "
                            class="mr-3"
                          >
                            {{ question.questionNumber }}
                          </div>
                          <survey-question-text
                            :text="question.text"
                          ></survey-question-text>
                        </div>
                      </v-card-title>
                      <v-divider></v-divider>
                      <div class="pa-6">
                        <div
                          v-for="child in question.children"
                          :key="child.key"
                        >
                          <div v-if="question.type === 'table'" class="mb-16">
                            <div class="font-weight-bold d-flex align-center">
                              <v-chip
                                v-if="
                                  child.questionNumber &&
                                    child.questionNumber.length > 0
                                "
                                color="secondary"
                                class="text-capitalize mr-2 flex-shrink-0"
                                >{{ child.questionNumber }}</v-chip
                              >
                              <survey-question-text
                                :text="child.text"
                              ></survey-question-text>
                            </div>
                            <di class="mt-3 mb-8">
                              <comparison-table
                                :options="question.options"
                                :questionType="question.type"
                                :questionKey="child.key"
                                :question="question"
                                :dataSets="datasets"
                              ></comparison-table>
                            </di>
                          </div>
                        </div>
                        <comparison-table
                          v-if="question.type === 'checklist'"
                          :question="question"
                          :questionType="question.type"
                          :questionKey="checklistQuestionKey(question.children)"
                          :options="checklistOptions(question.children)"
                          :dataSets="datasets"
                        ></comparison-table>
                        <comparison-table
                          v-if="!['table', 'checklist'].includes(question.type)"
                          :question="question"
                          :questionType="question.type"
                          :questionKey="question.key"
                          :options="question.options"
                          :dataSets="datasets"
                        ></comparison-table>
                      </div>
                    </v-card>
                  </template>
                </v-card-text>
              </div>
            </div>
          </v-container>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>
<style lang="scss" scoped>
@import "./scss/aggregate-report.scss";
.detail-accent {
  border-left: 0.5rem rgba(0, 0, 0, 0.25) solid;
}

.detail-icon {
  font-size: 32px;
  color: rgba(0, 0, 0, 0.25);
}
</style>

<script>
import { generatePDF, generateCSV } from "./Results.ItemAnalysis.Generators";
import ComparisonTable from "./Results.ItemAnalysis.ComparisonTable.vue";
import { alphaLabels } from "./Constants.js";
import { questionSettings } from "../../../constants/Survey";
import SurveyQuestionText from "../../../components/Survey.Question.Text.vue";

export default {
  components: { ComparisonTable, SurveyQuestionText },
  data: () => ({
    lastIntersectPosition: 0,
    scrollPosition: 0,
    currentSection: 0,
    titleBarHeight: 0,
    sectionDetails: [],
    selectedOptions: [
      {
        text: "True",
        value: "true",
        mappingValue: "true",
      },
      {
        text: "False",
        value: "false",
        mappingValue: "false",
      },
    ],
  }),
  props: {
    label: String,
    loading: Boolean,
    districtId: String,
    schoolId: String,
    show: Boolean,
    datasets: {
      type: Object,
      required: true,
    },
    useMapping: Boolean,
    hide: Array,
  },
  computed: {
    alphaLabels() {
      return alphaLabels;
    },
    district() {
      return this.$store.state.entities.data.find(
        (x) => x.id === this.districtId
      );
    },
    school() {
      let target = this.district;

      if (target && !target.children) return target;
      if (target && target.children)
        return target.children.find((x) => x.id === this.schoolId);
      return null;
    },
    survey() {
      let target = this.useMapping
        ? this.$store.getters["survey/mapped"]
        : this.$store.state.survey;

      if (!target || !target.categories) return { categories: [] };

      const removeHiddenQuestions = (questions) => {
        let i = questions.length - 1;

        while (i >= 0) {
          if (this.hide.some((x) => x === questions[i].key)) {
            questions.splice(i, 1);
          } else if (
            questions[i].children &&
            questions[i].children.length > 0
          ) {
            removeHiddenQuestions(questions[i].children);
          }

          if (questions[i]?.type === "textBlock") {
            questions.splice(i, 1);
          }

          i--;
        }
      };

      target = JSON.parse(JSON.stringify(target));

      if (this.hide && this.hide.length > 0) {
        for (let i = 0; i < target.categories.length; i++)
          removeHiddenQuestions(target.categories[i].questions);
      }

      return target;
    },
  },
  watch: {
    show(val) {
      if (val) {
        setTimeout(() => {
          this.scrollTo(0);
        });
        window.addEventListener("keyup", this.onEscapeKeyUp);
      } else {
        window.removeEventListener("keyup", this.onEscapeKeyUp);
      }
    },
    loading(val) {
      if (!val) {
        this.setScrollPosition();
      }
    },
  },
  methods: {
    checklistOptions(children) {
      let options = [];
      children.forEach((child, i) => {
        options.push({
          text: child.text,
          value: i,
          mappingValue: i,
          weight: 0,
        });
      });
      return options;
    },
    checklistQuestionKey(children){
      return children[0].key
    },
    setScrollPosition() {
      if (Array.isArray(this.$refs.section)) {
        this.sectionDetails = this.$refs.section.map((x) => {
          return {
            el: x,
            top: x.offsetTop,
            bottom: x.offsetTop + x.offsetHeight,
          };
        });

        let offset = this.$refs.content.offsetHeight * 0.5;
        this.scrollPosition = this.$refs.content.scrollTop;

        this.currentSection = this.sectionDetails.findIndex((x, i) => {
          return (
            (i === 0 && this.scrollPosition < x.top + 20) ||
            this.scrollPosition < x.top + offset ||
            this.scrollPosition < x.bottom - offset
          );
        });
      }
    },
    generatePDF() {
      generatePDF({
        school: this.school,
        district: this.district,
        survey: this.survey,
        datasets: this.datasets,
      });
    },
    generateCSV() {
      generateCSV({
        school: this.school,
        district: this.district,
        survey: this.survey,
        datasets: this.datasets,
      });
    },
    onEscapeKeyUp(e) {
      if (e.which === 27) {
        this.$emit("close-report");
      }
    },
    scrollTo(position) {
      this.setScrollPosition();
      if (this.$refs.content) this.$refs.content.scrollTop = position;
    },
    hidden(question) {
      if (!question || !question.settings) return false;

      let setting = question.settings.find(
        (x) => x.key === questionSettings.private.key
      );
      return setting ? setting.value === "true" : false;
    },
  },
};
</script>
